import axios from 'axios';

interface NotificationContext {
  clientId: string;
  appId: string;
  environment?: string;
  role?: string;
  externalUserId?: string;
}

interface UserContext extends NotificationContext {
  userAgent?: string;
  ipAddress?: string;
}

interface NotificationResponse {
  notifications: any[];
  count: number;
}

// Maine Stream CRM backend URL for notifications
// Note: Uses /api/api because NGINX strips the first /api prefix
const NOTIFICATION_API_BASE = process.env.REACT_APP_NOTIFICATION_API_URL || 'https://crm.maine-stream.com/api/api';

class SystemNotificationService {
  /**
   * Get active notifications for current user context
   */
  async getActiveNotifications(context: NotificationContext): Promise<NotificationResponse> {
    const params = new URLSearchParams({
      clientId: context.clientId,
      appId: context.appId,
      environment: context.environment || 'production',
      ...(context.role && { role: context.role }),
      ...(context.externalUserId && { externalUserId: context.externalUserId })
    });
    
    const response = await axios.get(`${NOTIFICATION_API_BASE}/notifications/active/for-user?${params}`);
    return response.data;
  }
  
  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string, userContext: UserContext) {
    const response = await axios.post(`${NOTIFICATION_API_BASE}/notifications/${notificationId}/read`, userContext);
    return response.data;
  }
  
  /**
   * Acknowledge notification
   */
  async acknowledge(notificationId: string, userContext: UserContext) {
    const response = await axios.post(`${NOTIFICATION_API_BASE}/notifications/${notificationId}/acknowledge`, userContext);
    return response.data;
  }
  
  /**
   * Dismiss notification
   */
  async dismiss(notificationId: string, userContext: UserContext) {
    const response = await axios.post(`${NOTIFICATION_API_BASE}/notifications/${notificationId}/dismiss`, userContext);
    return response.data;
  }
  
  /**
   * Check interaction status
   */
  async getInteractionStatus(notificationId: string, userContext: NotificationContext) {
    const params = new URLSearchParams({
      externalUserId: userContext.externalUserId || '',
      appId: userContext.appId,
      clientId: userContext.clientId
    });
    
    const response = await axios.get(`${NOTIFICATION_API_BASE}/notifications/${notificationId}/status?${params}`);
    return response.data;
  }
}

export default new SystemNotificationService();
